import * as storage from "utils/local-storage";
import { GROUP_SLUG, TOKEN, USER_ID, REFRESH_TOKEN } from "./constants";
import { getPrefixPath } from "./backend-path-builders";
import * as Sentry from "@sentry/react";

const headers = () => {
  return {
    "x-user-id": storage.session.getItem(USER_ID) || "",
    "x-login-token": storage.session.getItem(TOKEN) || "",
    "x-refresh-token": storage.session.getItem(REFRESH_TOKEN) || "",
    "x-client-id": storage.session.getItem(GROUP_SLUG) || "",
  };
};

// The backend's integrations auth middleware accepts a request as long as it
// carries EITHER x-user-id OR x-login-token. If both are empty the request is
// guaranteed to be rejected 401, so there's no point firing it — and doing so
// during a sessionStorage-clear race produces a burst of 401s (one failed
// "Unable to fetch balance" toast per in-flight request). Short-circuit such
// requests with a rejected promise instead of hitting the network.
class MissingIntegrationAuthError extends Error {
  status = 401;
  sentryReported = true;
  constructor() {
    super("Skipped integrated request: missing auth headers");
    this.name = "MissingIntegrationAuthError";
  }
}

const hasIntegrationAuth = () => {
  const h = headers();
  return h["x-user-id"] !== "" || h["x-login-token"] !== "";
};

const groupSlug = () => {
  return "/" + storage.session.getItem(GROUP_SLUG);
};

// --- Transient-failure retry -------------------------------------------------
//
// Integrated sessions run inside the partner Mini App's native WebView
// (iOS WKWebView / Android WebView). Those WebViews intermittently drop
// in-flight HTTP requests: the fetch() promise rejects with a TypeError
// ("Network Error" / "Load failed") and the request never reaches our backend.
// Sentry shows this as the single highest-volume frontend error, and the
// breadcrumb trail confirms it's transient — the very next identical request
// succeeds (200). Without a retry the first such drop surfaces as a user-facing
// toast ("Unable to fetch balance.") even though nothing is actually wrong.
//
// We retry two distinct failure classes, with different safety rules:
//   1. Network drops (fetch rejected — request never completed). Always safe to
//      retry: the server never saw it. Retried for every method.
//   2. Server 5xx (request reached the server, server errored). Safe to retry
//      only for idempotent methods (GET/PUT). POST is NOT retried on 5xx — the
//      server may have already processed it (e.g. placed a bet / created an
//      order) and a blind retry could duplicate it. POST is still retried on
//      network drops, because those never reached the server.
//
// 4xx is never retried (deterministic: bad request, auth, not-found).

const MAX_RETRIES = 2; // up to 3 total attempts
const RETRY_BASE_DELAY_MS = 250;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

class HttpError extends Error {
  status: number;
  sentryReported = true;
  response: { status: number; statusText: string; data: any; url: string };
  constructor(status: number, statusText: string, data: any, url: string) {
    super(`HTTP ${status}: ${statusText}`);
    this.name = "HttpError";
    this.status = status;
    this.response = { status, statusText, data, url };
  }
}

const reportToSentryOnce = (
  error: any,
  method: string,
  path: string,
  status: number | "network",
  requestBody: any,
  attempts: number
) => {
  Sentry.captureException(error, {
    tags: {
      feature: "integrated_http_client",
      http_method: method,
      http_status: String(status),
    },
    extra: {
      path,
      method,
      status,
      attempts,
      requestBody,
      userId: storage.session.getItem(USER_ID),
      groupSlug: storage.session.getItem(GROUP_SLUG),
    },
  });
};

const parseBody = async (response: Response) => {
  try {
    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  } catch {
    return null;
  }
};

interface RequestConfig {
  method: "GET" | "POST" | "PUT";
  path: string;
  body?: any;
  reportToSentry: boolean;
  // Whether the JSON parse of a successful body is required (GET/PUT) or the
  // empty-body-tolerant POST variant.
  tolerateEmptyBody?: boolean;
  // Callers that run their own polling loop and treat a 5xx as an expected
  // "not ready yet" signal (e.g. bet-detail polling after placing a bet) opt
  // out of the built-in retry so they keep full control of timing.
  skipRetry?: boolean;
}

const performRequest = async (config: RequestConfig): Promise<any> => {
  const { method, path, body, reportToSentry } = config;
  const url = process.env.REACT_APP_BACKEND_URL + getPrefixPath() + groupSlug() + path;
  // 5xx is only safe to retry when the request is idempotent. A POST may have
  // already mutated server state (bet placed, order created), so we never
  // replay it on a server error — only on a network drop (handled below).
  const retryOn5xx = method !== "POST";
  const maxRetries = config.skipRetry ? 0 : MAX_RETRIES;

  let lastError: any;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (attempt > 0) {
      await delay(RETRY_BASE_DELAY_MS * 2 ** (attempt - 1));
    }

    let response: Response;
    try {
      response = await fetch(url, {
        method,
        headers: headers(),
        ...(method === "GET" ? {} : { body: JSON.stringify(body) }),
      });
    } catch (err) {
      // Network drop — request never reached the server. Always retryable.
      lastError = err;
      if (attempt < maxRetries) continue;
      if (reportToSentry) {
        reportToSentryOnce(err, method, path, "network", body, attempt + 1);
      }
      throw err;
    }

    if (response.ok) {
      if (config.tolerateEmptyBody) {
        const text = await response.text();
        return text ? JSON.parse(text) : null;
      }
      return response.json();
    }

    // Reached the server but got an error status.
    const data = await parseBody(response);
    const httpError = new HttpError(response.status, response.statusText, data, response.url);
    lastError = httpError;

    const retryable = response.status >= 500 && response.status <= 599 && retryOn5xx;
    if (retryable && attempt < maxRetries) continue;

    // Terminal. Report non-network server errors to Sentry (4xx like the
    // "no confirmation selected" 400 is expected flow noise — skip it).
    const isClientError = response.status >= 400 && response.status < 500;
    if (reportToSentry && !isClientError) {
      reportToSentryOnce(httpError, method, path, response.status, body, attempt + 1);
    }
    throw httpError;
  }

  throw lastError;
};

export const integratedGet = (path: string, options: any) => {
  if (!hasIntegrationAuth()) return Promise.reject(new MissingIntegrationAuthError());
  return performRequest({
    method: "GET",
    path,
    reportToSentry: options?.reportErrorToSentry !== false,
    skipRetry: options?.skipRetry === true,
  });
};

export const integratedPost = (path: string, options: any) => {
  if (!hasIntegrationAuth()) return Promise.reject(new MissingIntegrationAuthError());
  return performRequest({
    method: "POST",
    path,
    body: options?.body,
    reportToSentry: options?.reportErrorToSentry !== false,
    tolerateEmptyBody: true,
    skipRetry: options?.skipRetry === true,
  });
};

export const integratedPut = (path: string, options: { body?: any; reportErrorToSentry?: boolean; skipRetry?: boolean }) => {
  if (!hasIntegrationAuth()) return Promise.reject(new MissingIntegrationAuthError());
  return performRequest({
    method: "PUT",
    path,
    body: options?.body,
    reportToSentry: options?.reportErrorToSentry !== false,
    skipRetry: options?.skipRetry === true,
  });
};
